import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import * as amplitude from '@amplitude/analytics-browser';
import { db, storage } from '../firebase';
import {
  deleteAgentConversation,
  getSignedAgentConversationAudioUrl,
  updateAgentConversation,
} from '../services/agentConversationService';

export default function useAgentConversationDetails(agentId, conversationId) {
  const [agent, setAgent] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [sessionReport, setSessionReport] = useState(null);
  const [canonicalAgentId, setCanonicalAgentId] = useState(agentId || '');
  const [redirectPath, setRedirectPath] = useState('');

  useEffect(() => {
    if (!conversationId) {
      setLoading(false);
      setError('Conversation not found.');
      return undefined;
    }

    amplitude.track('Agent Conversation Page Viewed', { agentId, conversationId });

    let isMounted = true;
    const conversationRef = doc(db, 'recordings', conversationId);

    const unsubscribeConversation = onSnapshot(
      conversationRef,
      async (snapshot) => {
        if (!isMounted) {
          return;
        }

        if (!snapshot.exists()) {
          setConversation(null);
          setError('Conversation not found.');
          setLoading(false);
          return;
        }

        const data = snapshot.data() || {};
        const resolvedAgentId = data?.metadata?.agentId || agentId || '';

        if (data.source !== 'agent') {
          setRedirectPath(`/recording-details/${encodeURIComponent(conversationId)}`);
          setLoading(false);
          return;
        }

        setRedirectPath('');
        setConversation({ id: snapshot.id, ...data });
        setCanonicalAgentId(resolvedAgentId);
        setError(null);
        setLoading(false);

        if (data.status === 'transcribed' && data.filePath) {
          try {
            const inlineAudioUrl = typeof data?.metadata?.audioDownloadUrl === 'string'
              ? data.metadata.audioDownloadUrl.trim()
              : '';
            const nextAudioUrl = inlineAudioUrl || await getSignedAgentConversationAudioUrl({ conversationId });
            if (isMounted) {
              setAudioUrl(nextAudioUrl || null);
            }
          } catch (audioError) {
            try {
              const storageAudioUrl = await getDownloadURL(ref(storage, data.filePath));
              if (isMounted) {
                setAudioUrl(storageAudioUrl || null);
              }
            } catch (storageAudioError) {
              console.error('Error fetching agent conversation audio URL:', audioError);
              console.error('Error fetching agent conversation audio from storage:', storageAudioError);
              if (isMounted) {
                setAudioUrl(null);
              }
            }
          }
        } else if (isMounted) {
          setAudioUrl(null);
        }
      },
      (snapshotError) => {
        if (!isMounted) {
          return;
        }

        console.error('Error listening to agent conversation:', snapshotError);
        setError('Error fetching conversation details');
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      unsubscribeConversation();
    };
  }, [agentId, conversationId]);

  useEffect(() => {
    const sessionReportPath = typeof conversation?.metadata?.sessionReportPath === 'string'
      ? conversation.metadata.sessionReportPath.trim()
      : '';

    if (!sessionReportPath) {
      setSessionReport(null);
      return undefined;
    }

    let isMounted = true;

    const fetchSessionReport = async () => {
      try {
        const reportUrl = await getDownloadURL(ref(storage, sessionReportPath));
        const response = await fetch(reportUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch session report: ${response.status}`);
        }

        const payload = await response.json();
        if (isMounted) {
          setSessionReport(payload && typeof payload === 'object' ? payload : null);
        }
      } catch (reportError) {
        console.error('Error fetching agent session report:', reportError);
        if (isMounted) {
          setSessionReport(null);
        }
      }
    };

    fetchSessionReport();

    return () => {
      isMounted = false;
    };
  }, [conversation?.metadata?.sessionReportPath]);

  useEffect(() => {
    if (!canonicalAgentId) {
      setAgent(null);
      return undefined;
    }

    let isMounted = true;
    const agentRef = doc(db, 'agents', canonicalAgentId);

    const unsubscribeAgent = onSnapshot(
      agentRef,
      (snapshot) => {
        if (!isMounted) {
          return;
        }

        if (!snapshot.exists()) {
          setAgent(null);
          return;
        }

        setAgent({ id: snapshot.id, ...snapshot.data() });
      },
      (snapshotError) => {
        console.error('Error listening to agent details:', snapshotError);
      }
    );

    return () => {
      isMounted = false;
      unsubscribeAgent();
    };
  }, [canonicalAgentId]);

  const handleDeleteConversation = async () => {
    if (!conversation) {
      return;
    }

    await deleteAgentConversation({
      conversationId,
      conversation,
    });

    amplitude.track('Agent Conversation Deleted', {
      agentId: canonicalAgentId,
      conversationId,
    });
  };

  const handleUpdateConversation = async (updates) => {
    await updateAgentConversation({ conversationId, updates });
    amplitude.track('Agent Conversation Updated', {
      agentId: canonicalAgentId,
      conversationId,
      updates,
    });
  };

  return {
    agent,
    conversation,
    loading,
    error,
    audioUrl,
    sessionReport,
    canonicalAgentId,
    redirectPath,
    deleteConversation: handleDeleteConversation,
    updateConversation: handleUpdateConversation,
  };
}
